import { environment } from '@src/environments/environment';
import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { LoginOtpRequest, OtpAuthorisationService } from '@src/app/shared/backend/mfa';
import { SessionService } from '@app/shared/services/session.service';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { LoginNavigationService } from '@src/app/shared/services/loginNavigation.service';

interface ParentOtpMessage {
  username: string;
  otp: string;
}

interface OtpLoginResponse {
  accessToken: string;
  apiToken: string;
  refreshToken: string;
}

interface OtpLoginApiResponse {
  authenticationResult: OtpLoginResponse;
}

@Component({
    selector: 'app-otp-transparent-login',
    templateUrl: './otp-transparent-login.component.html',
    styleUrls: ['./otp-transparent-login.component.scss'],
    standalone: false
})
export class OtpTransparentLoginComponent implements OnInit, OnDestroy {
  statusMessage = '';

  private readonly allowedParentOrigins = new Set<string>(
    environment.allowedParentOriginOTP
      .split(';')
      .map(origin => origin.trim())
  );

  constructor(
    public translate: TranslateService,
    private otpAuthorisationService: OtpAuthorisationService,
    private sessionService: SessionService,
    private permissionService: PermissionService,
    private loginNavigationService: LoginNavigationService,
    private zone: NgZone,
    private router: Router
  ) { }

  async ngOnInit(): Promise<void> {
    console.log('OTP login component loaded.');
    this.sessionService.resetLocalSession();

    console.log('Allowed parent origins', [...this.allowedParentOrigins]);

    console.log('OTP login component: waiting for OTP message');

    window.addEventListener('message', this.handleParentMessage);
  }

  ngAfterViewChecked(): void {
    this.statusMessage = this.translate.instant('OTP.IFRAME_WAITING_OTP');
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.handleParentMessage);
  }

  private readonly handleParentMessage = (event: MessageEvent): void => {
    console.log('OTP login component: postMessage received', {
      origin: event.origin,
      expectedOrigins: [...this.allowedParentOrigins],
      data: event.data
    });

    if (!this.allowedParentOrigins.has(event.origin)) {
      console.warn('OTP login component: blocked message from origin', {
        receivedOrigin: event.origin,
        allowedOrigins: [...this.allowedParentOrigins]
      });

      return;
    }

    const otpMessage = this.getTransparentLoginOtpMessage(event.data);

    if (!otpMessage) {
      console.warn(
        'OTP login component: unexpected postMessage payload',
        event.data
      );
      return;
    }

    this.zone.run(async () => {
      await this.completeOtpLogin(otpMessage);
    });
  };

  private async completeOtpLogin(message: ParentOtpMessage): Promise<void> {
    try {
      this.sessionService.setLoading(true);
      this.statusMessage = this.translate.instant('OTP.IFRAME_OTP_RECEIVED');

      const loginResponse = await this.loginWithOtp(message);

      this.sessionService.applyTokens(loginResponse.authenticationResult);
      await this.sessionService.getUser();

      this.sessionService.hasAccess = true;

      this.statusMessage = this.translate.instant('OTP.IFRAME_OTP_COMPLETED');

      await this.redirectAfterSuccessfulLogin();
    } catch (error) {
      console.error('OTP login component: transparent login failed', error);

      this.statusMessage = this.translate.instant('OTP.IFRAME_OTP_FAILED');

      await this.router.navigate(['/embedded/error']);
    } finally {
      this.sessionService.setLoading(false);
    }
  }

  private async loginWithOtp(message: ParentOtpMessage): Promise<OtpLoginApiResponse> {
    const payload: LoginOtpRequest = {
      username: message.username,
      otp: message.otp
    };

    const response = await firstValueFrom(this.otpAuthorisationService.apiOtpAuthorisationLoginPost(payload));

    console.log('OTP login component: backend auth response received', response);

    return response;
  }

  private async redirectAfterSuccessfulLogin(): Promise<void> {
    const path = await this.loginNavigationService.resolveLandingPage();

    // pass hideBackButton flag for OTP users (iframe)
    await this.router.navigate([path], {
      replaceUrl: true,
      state: {
        hideBackButton: true
      }
    });
  }

  private getTransparentLoginOtpMessage(value: unknown): ParentOtpMessage | null {
    if (typeof value !== 'object' || value === null) {
      return null;
    }

    const username = Reflect.get(value, 'username');
    const otp = Reflect.get(value, 'otp');

    if (typeof username !== 'string' ||typeof otp !== 'string') {
      return null;
    }

    return {
      username,
      otp
    };
  }
}
